import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Menu,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Save as SaveIcon,
    ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import MagnetSelector from "./MagnetSelector";
import SimpleFloorPlan from "./SimpleFloorPlan";
import TestPointSelector from "./TestPointSelector";
import FrequencyEntry from "./FrequencyEntry";
import ScoreEntry from "./ScoreEntry";
import NotesEntry from "./NotesEntry";
import ReportService from "../../../services/Report.service";

// Steps: 0=project+meta, 1=magnet, 2=floorplan, 3=testpoints,
//        4=frequencies, 5=scores, 6=notes, 7=done
const TOTAL_STEPS = 7;

const REPORT_TYPE_MAP = { B: "Baseline", P: "Preliminary", F: "Final" };
const REPORT_TYPE_REVERSE = { Baseline: "B", Preliminary: "P", Final: "F" };

const STEP_LABELS = [
    "Project",
    "Magnet",
    "Floor Plan",
    "Test Points",
    "Frequencies",
    "Scores",
    "Notes",
];

const EMPTY_FORM = {
    linkedTo: "project",
    project: null,
    quote: null,
    reportType: "", // "B", "P", or "F"
    location: "",
    witness: true,
    witnessName: "",
    witnessCompany: "",
    magnet_type: "",
    magnet_strength: "",
    floorPlanData: null,
    testPoints: [],
    frequencies: [],
    scores: {},
    targetAttenuation: 100,
    notes: "",
};

export default function RFTestReportFormDialog({
    open,
    onClose,
    item,
    initialProject,
    initialQuote,
    initialLinkedTo,
    onSaved,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [mode, setMode] = useState("create"); // "create" | "load"
    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

    // Load-mode state
    const [loadedReportId, setLoadedReportId] = useState(null);
    const [availableReports, setAvailableReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [loadingReports, setLoadingReports] = useState(false);

    // Copy-floor-plan-from-sibling-report state
    const [floorPlanKey, setFloorPlanKey] = useState(0);
    const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
    const [copySourceReports, setCopySourceReports] = useState([]);
    const [loadingCopySources, setLoadingCopySources] = useState(false);
    const [pendingCopyReport, setPendingCopyReport] = useState(null);

    const isEditing = Boolean(item);
    const snapshotRef = useRef(null);
    const isDirty =
        snapshotRef.current !== null &&
        JSON.stringify(form) !== snapshotRef.current;

    // Reset / hydrate when the dialog opens
    useEffect(() => {
        if (!open) return;
        setError("");
        setLoadingReports(false);

        if (item) {
            // Edit existing report — skip mode selection, load data directly
            const rt = REPORT_TYPE_REVERSE[item.test_type] || "";
            let parsedFloorPlan = item.floor_plan;
            if (typeof parsedFloorPlan === "string" && parsedFloorPlan) {
                try {
                    parsedFloorPlan = JSON.parse(parsedFloorPlan);
                } catch {
                    parsedFloorPlan = null;
                }
            }
            const hydratedForm = {
                linkedTo: item.project_detail ? "project" : "quote",
                project: item.project_detail
                    ? { id: item.project, ...item.project_detail }
                    : null,
                quote: item.quote_detail
                    ? { id: item.quote, ...item.quote_detail }
                    : null,
                reportType: rt,
                location: item.location || "",
                witness: !!(item.witness_name || item.witness_company),
                witnessName: item.witness_name || "",
                witnessCompany: item.witness_company || "",
                magnet_type: item.magnet_type || "",
                magnet_strength: item.magnet_strength || "",
                floorPlanData: parsedFloorPlan || null,
                testPoints: item.test_points || [],
                frequencies: item.frequencies || [],
                scores: item.scores || {},
                targetAttenuation: item.target_attenuation ?? 100,
                notes: item.notes || "",
            };
            setForm(hydratedForm);
            snapshotRef.current = JSON.stringify(hydratedForm);
            setMode("load");
            setActiveStep(0);
            setLoadedReportId(item.id);
            setAvailableReports([item]);
            setSelectedReport(item);
        } else {
            setForm({
                ...EMPTY_FORM,
                linkedTo: initialLinkedTo || EMPTY_FORM.linkedTo,
                project: initialProject || null,
                quote: initialQuote || null,
            });
            snapshotRef.current = null;
            setMode("create");
            setActiveStep(0);
            setLoadedReportId(null);
            setAvailableReports([]);
            setSelectedReport(null);
        }
    }, [open, item, initialProject, initialQuote, initialLinkedTo]);

    // ── Helpers ───────────────────────────────────────────────────────────────

    const setField = (field) => (value) =>
        setForm((f) => ({ ...f, [field]: value }));

    const handleTargetAttenuationChange = (value) => {
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
            setField("targetAttenuation")(value);
        }
    };

    // Scores are keyed "<freqIdx>-<testPoint>" with no lifecycle tie to
    // testPoints, so removing a point leaves its scores stranded unless we
    // prune them here — otherwise they still show up on the PDF printout.
    const handleTestPointsChange = (newTestPoints) => {
        setForm((f) => {
            const validPoints = new Set(newTestPoints);
            const prunedScores = Object.fromEntries(
                Object.entries(f.scores).filter(([key]) =>
                    validPoints.has(key.slice(key.indexOf("-") + 1)),
                ),
            );
            return { ...f, testPoints: newTestPoints, scores: prunedScores };
        });
    };

    const isTargetAttenuationValid = () => {
        if (form.targetAttenuation === "") return false;
        const parsed = Number(form.targetAttenuation);
        return Number.isFinite(parsed) && parsed >= 0;
    };

    const handleNext = () => setActiveStep((s) => s + 1);
    const handleBack = () => setActiveStep((s) => s - 1);

    const handleLinkedToChange = (_, val) => {
        if (!val) return;
        setForm((f) => ({ ...f, linkedTo: val, project: null, quote: null }));
        setAvailableReports([]);
        setSelectedReport(null);
        setLoadedReportId(null);
    };

    // ── Load-mode helpers ─────────────────────────────────────────────────────

    const clearReportFields = () => {
        setForm((f) => ({
            ...f,
            reportType: "",
            location: "",
            magnet_type: "",
            magnet_strength: "",
            floorPlanData: null,
            testPoints: [],
            frequencies: [],
            scores: {},
            targetAttenuation: 100,
            witnessName: "",
            witnessCompany: "",
            notes: "",
            witness: true,
        }));
    };

    const loadSpecificReport = (report) => {
        const rt = REPORT_TYPE_REVERSE[report.test_type] || "";
        let parsedFloorPlan = report.floor_plan;
        if (typeof parsedFloorPlan === "string" && parsedFloorPlan) {
            try {
                parsedFloorPlan = JSON.parse(parsedFloorPlan);
            } catch {
                parsedFloorPlan = null;
            }
        }
        setLoadedReportId(report.id);
        const merged = {
            ...form,
            reportType: rt,
            location: report.location || "",
            magnet_type: report.magnet_type || "",
            magnet_strength: report.magnet_strength || "",
            floorPlanData: parsedFloorPlan || null,
            testPoints: report.test_points || [],
            frequencies: report.frequencies || [],
            scores: report.scores || {},
            targetAttenuation: report.target_attenuation ?? 100,
            witnessName: report.witness_name || "",
            witnessCompany: report.witness_company || "",
            notes: report.notes || "",
            witness: !!(report.witness_name || report.witness_company),
        };
        setForm(merged);
        snapshotRef.current = JSON.stringify(merged);
    };

    const fetchReportsForItem = async (itemField, itemValue) => {
        if (!itemValue) return;
        setLoadingReports(true);
        setAvailableReports([]);
        setSelectedReport(null);
        setLoadedReportId(null);
        clearReportFields();
        try {
            const res = await ReportService.listRFTestReports({
                [itemField]: itemValue.id,
            });
            const all = Array.isArray(res.data)
                ? res.data
                : (res.data?.results ?? []);
            setAvailableReports(all);
            if (all.length === 1) {
                loadSpecificReport(all[0]);
                setSelectedReport(all[0]);
            }
        } catch {
            // silently ignore fetch errors
        } finally {
            setLoadingReports(false);
        }
    };

    // ── Copy floor plan from a sibling report ────────────────────────────────

    const openCopyFloorPlanMenu = async (e) => {
        const anchor = e.currentTarget;
        setCopyMenuAnchor(anchor);
        const linkedItem = form.linkedTo === "project" ? form.project : form.quote;
        if (!linkedItem) return;
        setLoadingCopySources(true);
        setCopySourceReports([]);
        try {
            const res = await ReportService.listRFTestReports({
                [form.linkedTo]: linkedItem.id,
            });
            const all = Array.isArray(res.data)
                ? res.data
                : (res.data?.results ?? []);
            const withFloorPlans = all.filter(
                (r) => r.id !== loadedReportId && r.floor_plan,
            );
            setCopySourceReports(withFloorPlans);
        } catch {
            setCopySourceReports([]);
        } finally {
            setLoadingCopySources(false);
        }
    };

    const closeCopyFloorPlanMenu = () => setCopyMenuAnchor(null);

    const applyFloorPlanCopy = (report) => {
        let parsed = report.floor_plan;
        if (typeof parsed === "string" && parsed) {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                parsed = null;
            }
        }
        if (parsed) {
            setField("floorPlanData")(parsed);
            // Force SimpleFloorPlan to remount so it re-loads from the new
            // initialData — it only hydrates from initialData once per mount.
            setFloorPlanKey((k) => k + 1);
        }
    };

    const handleSelectCopySource = (report) => {
        closeCopyFloorPlanMenu();
        const hasExistingShapes = form.floorPlanData?.shapes?.length > 0;
        if (hasExistingShapes) {
            setPendingCopyReport(report);
        } else {
            applyFloorPlanCopy(report);
        }
    };

    const confirmCopyFloorPlan = () => {
        if (pendingCopyReport) applyFloorPlanCopy(pendingCopyReport);
        setPendingCopyReport(null);
    };

    // ── Picker change handlers ────────────────────────────────────────────────

    const handleChangeProject = (newValue) => {
        setForm((f) => ({ ...f, project: newValue, quote: null }));
        if (mode === "load") {
            if (newValue) {
                fetchReportsForItem("project", newValue);
            } else {
                setAvailableReports([]);
                setSelectedReport(null);
                setLoadedReportId(null);
                clearReportFields();
            }
        }
    };

    const handleChangeQuote = (newValue) => {
        setForm((f) => ({ ...f, quote: newValue, project: null }));
        if (mode === "load") {
            if (newValue) {
                fetchReportsForItem("quote", newValue);
            } else {
                setAvailableReports([]);
                setSelectedReport(null);
                setLoadedReportId(null);
                clearReportFields();
            }
        }
    };

    // ── Step validation ───────────────────────────────────────────────────────

    const isStep0Valid = () => {
        const selectedItem = form.project || form.quote;
        if (!selectedItem) return false;
        if (mode === "create") {
            if (!form.reportType) return false;
            if (form.witness && (!form.witnessName || !form.witnessCompany))
                return false;
        }
        if (mode === "load") {
            if (loadingReports) return false;
            if (availableReports.length === 0) return false;
            if (availableReports.length > 1 && !selectedReport) return false;
        }
        return true;
    };

    const isNextDisabled = () => {
        switch (activeStep) {
            case 0:
                return !isStep0Valid();
            case 1:
                return !form.magnet_type;
            case 2:
                return !form.floorPlanData;
            case 3:
                return form.testPoints.length === 0;
            case 4:
                return !form.frequencies.some(
                    (f) => f.frequency && f.reference !== "",
                );
            case 5:
                return !isTargetAttenuationValid();
            default:
                return false;
        }
    };

    // ── Save ──────────────────────────────────────────────────────────────────

    const handleSave = async ({ closeOnSuccess = false } = {}) => {
        const selectedItem = form.project || form.quote;
        const reportNumber = selectedItem
            ? `${selectedItem.number}-${form.reportType}`
            : "";
        const parsedTargetAttenuation = Number(form.targetAttenuation);

        const payload = {
            project: form.linkedTo === "project" ? form.project?.id : null,
            quote: form.linkedTo === "quote" ? form.quote?.id : null,
            report_number: reportNumber,
            test_type: REPORT_TYPE_MAP[form.reportType] || form.reportType,
            location: form.location,
            magnet_type: form.magnet_type,
            magnet_strength:
                form.magnet_type === "GE" ? form.magnet_strength : null,
            floor_plan: form.floorPlanData
                ? JSON.stringify(form.floorPlanData)
                : null,
            witness_name: form.witness ? form.witnessName : "",
            witness_company: form.witness ? form.witnessCompany : "",
            test_points: form.testPoints,
            frequencies: form.frequencies,
            scores: form.scores,
            target_attenuation:
                Number.isFinite(parsedTargetAttenuation) &&
                parsedTargetAttenuation >= 0
                    ? parsedTargetAttenuation
                    : 100,
            notes: form.notes,
        };

        setLoading(true);
        setError("");
        try {
            if (loadedReportId) {
                await ReportService.updateRFTestReport(loadedReportId, payload);
            } else {
                await ReportService.createRFTestReport(payload);
            }
            onSaved();
            if (closeOnSuccess) {
                onClose();
            } else {
                handleNext(); // advance to completion screen
            }
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                JSON.stringify(err?.response?.data) ||
                "An error occurred.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSave = () => handleSave({ closeOnSuccess: true });

    const requestClose = () => {
        if (isEditing && isDirty) {
            setUnsavedDialogOpen(true);
        } else {
            onClose();
        }
    };

    // ── Step content ──────────────────────────────────────────────────────────

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                            {mode === "load"
                                ? "Select an item to load its existing report:"
                                : "Select an item and report type:"}
                        </Typography>

                        {/* Link-to toggle */}
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Link to
                            </Typography>
                            <ToggleButtonGroup
                                value={form.linkedTo}
                                exclusive
                                onChange={handleLinkedToChange}
                                size="small"
                                sx={{ display: "flex", mt: 0.5, mb: 2 }}
                            >
                                <ToggleButton value="project" sx={{ flex: 1 }}>
                                    Project
                                </ToggleButton>
                                <ToggleButton value="quote" sx={{ flex: 1 }}>
                                    Quote
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* Item picker */}
                        {form.linkedTo === "project" ? (
                            <ProjectPicker
                                value={form.project}
                                onChange={handleChangeProject}
                            />
                        ) : (
                            <QuotePicker
                                value={form.quote}
                                onChange={handleChangeQuote}
                            />
                        )}

                        {/* Load mode: multiple reports selector */}
                        {mode === "load" && availableReports.length > 1 && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="medium"
                                >
                                    Select a Report to Load:
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel>Available Reports</InputLabel>
                                    <Select
                                        value={
                                            selectedReport
                                                ? selectedReport.id
                                                : ""
                                        }
                                        label="Available Reports"
                                        onChange={(e) => {
                                            const r = availableReports.find(
                                                (r) => r.id === e.target.value,
                                            );
                                            if (r) {
                                                setSelectedReport(r);
                                                loadSpecificReport(r);
                                            }
                                        }}
                                    >
                                        {availableReports.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>
                                                {r.report_number ||
                                                    `Report #${r.id}`}{" "}
                                                - {r.test_type}
                                                {r.created &&
                                                    ` (${new Date(
                                                        r.created,
                                                    ).toLocaleDateString()})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Paper>
                        )}

                        {/* Load mode: no reports found warning */}
                        {mode === "load" &&
                            (form.project || form.quote) &&
                            !loadingReports &&
                            availableReports.length === 0 && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                        borderColor: "warning.main",
                                        bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? "rgba(255, 167, 38, 0.15)"
                                                : "rgba(237, 108, 2, 0.08)",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: "warning.dark",
                                            fontWeight: "medium",
                                        }}
                                        gutterBottom
                                    >
                                        No Reports Found
                                    </Typography>
                                    <Typography variant="body2">
                                        No existing RF Test Reports were found
                                        for this item. Switch to "Create New
                                        Report" mode.
                                    </Typography>
                                </Paper>
                            )}

                        {/* Report type + witness — shown in create mode, or in
                            load mode once a specific report is loaded */}
                        {(mode === "create" ||
                            (mode === "load" && loadedReportId)) && (
                            <>
                                <FormControl fullWidth sx={{ mt: 3 }}>
                                    <InputLabel>Report Type</InputLabel>
                                    <Select
                                        value={form.reportType}
                                        label="Report Type"
                                        onChange={(e) =>
                                            setField("reportType")(
                                                e.target.value,
                                            )
                                        }
                                    >
                                        <MenuItem value="B">Baseline</MenuItem>
                                        <MenuItem value="P">
                                            Preliminary
                                        </MenuItem>
                                        <MenuItem value="F">Final</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Location (Room)"
                                    placeholder="e.g. MRI 1, Suite B"
                                    value={form.location}
                                    onChange={(e) =>
                                        setField("location")(e.target.value)
                                    }
                                    sx={{ mt: 3 }}
                                    helperText="Disambiguates multiple rooms tested on the same project"
                                />

                                <Box sx={{ mt: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={form.witness}
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;
                                                    setForm((f) => ({
                                                        ...f,
                                                        witness: checked,
                                                        witnessName: checked
                                                            ? f.witnessName
                                                            : "",
                                                        witnessCompany: checked
                                                            ? f.witnessCompany
                                                            : "",
                                                    }));
                                                }}
                                            />
                                        }
                                        label="Include Witness"
                                    />
                                    {form.witness && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label="Witness Name"
                                                value={form.witnessName}
                                                onChange={(e) =>
                                                    setField("witnessName")(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label="Witness Company"
                                                value={form.witnessCompany}
                                                onChange={(e) =>
                                                    setField("witnessCompany")(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                );

            case 1:
                return (
                    <MagnetSelector
                        magnetType={form.magnet_type}
                        magnetStrength={form.magnet_strength}
                        onChange={({ magnet_type, magnet_strength }) =>
                            setForm((f) => ({
                                ...f,
                                magnet_type,
                                magnet_strength: magnet_strength || "",
                            }))
                        }
                    />
                );

            case 2:
                return (
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mb: 1,
                            }}
                        >
                            <Button
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={openCopyFloorPlanMenu}
                                disabled={
                                    !(form.linkedTo === "project"
                                        ? form.project
                                        : form.quote)
                                }
                            >
                                Copy From Another Report
                            </Button>
                            <Menu
                                anchorEl={copyMenuAnchor}
                                open={Boolean(copyMenuAnchor)}
                                onClose={closeCopyFloorPlanMenu}
                            >
                                {loadingCopySources && (
                                    <MenuItem disabled>Loading…</MenuItem>
                                )}
                                {!loadingCopySources &&
                                    copySourceReports.length === 0 && (
                                        <MenuItem disabled>
                                            No other reports with a floor plan
                                        </MenuItem>
                                    )}
                                {!loadingCopySources &&
                                    copySourceReports.map((r) => (
                                        <MenuItem
                                            key={r.id}
                                            onClick={() =>
                                                handleSelectCopySource(r)
                                            }
                                        >
                                            {r.report_number ||
                                                `Report #${r.id}`}{" "}
                                            - {r.test_type}
                                            {r.location && ` (${r.location})`}
                                            {r.created &&
                                                ` — ${new Date(
                                                    r.created,
                                                ).toLocaleDateString()}`}
                                        </MenuItem>
                                    ))}
                            </Menu>
                        </Box>
                        <SimpleFloorPlan
                            key={floorPlanKey}
                            initialData={form.floorPlanData}
                            onChange={setField("floorPlanData")}
                        />
                    </Box>
                );

            case 3:
                return (
                    <TestPointSelector
                        testPoints={form.testPoints}
                        onChange={handleTestPointsChange}
                    />
                );

            case 4:
                return (
                    <FrequencyEntry
                        frequencies={form.frequencies}
                        magnetType={form.magnet_type}
                        magnetStrength={form.magnet_strength}
                        onChange={setField("frequencies")}
                    />
                );

            case 5:
                return (
                    <ScoreEntry
                        testPoints={form.testPoints}
                        frequencies={form.frequencies}
                        scores={form.scores}
                        onChange={setField("scores")}
                        targetAttenuation={form.targetAttenuation}
                        onTargetAttenuationChange={
                            handleTargetAttenuationChange
                        }
                    />
                );

            case 6:
                return (
                    <NotesEntry
                        notes={form.notes}
                        onChange={setField("notes")}
                    />
                );

            default:
                return null;
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const selectedItem = form.project || form.quote;

    return (
        <>
        <Dialog
            open={open}
            onClose={requestClose}
            maxWidth="lg"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: fullScreen
                    ? { height: "100dvh", maxHeight: "100dvh" }
                    : { height: "90vh" },
            }}
        >
            <DialogTitle>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box>
                        <Typography variant="h6">RF Test Report</Typography>
                        {selectedItem && (
                            <Typography variant="body2" color="text.secondary">
                                {form.project &&
                                    `Project: ${form.project.number} – ${form.project.name}`}
                                {form.quote &&
                                    `Quote: ${form.quote.number} – ${form.quote.name}`}
                            </Typography>
                        )}
                        {form.reportType && selectedItem && (
                            <Typography
                                variant="body2"
                                color="primary"
                                fontWeight={600}
                            >
                                Report #: {selectedItem.number}-
                                {form.reportType}
                                {form.location && ` · ${form.location}`}
                            </Typography>
                        )}
                    </Box>
                    {activeStep < TOTAL_STEPS && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Step {activeStep + 1} of {TOTAL_STEPS}
                        </Typography>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ flex: 1, overflowY: "auto" }}>
                {activeStep < TOTAL_STEPS && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            overflowX: "auto",
                            pb: 0.5,
                        }}
                    >
                        {STEP_LABELS.map((label, idx) => {
                            const isActive = idx === activeStep;
                            const isDone = idx < activeStep;
                            const canJump = isEditing && !loading;
                            return (
                                <React.Fragment key={label}>
                                    <Box
                                        onClick={
                                            canJump
                                                ? () => setActiveStep(idx)
                                                : undefined
                                        }
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 0.25,
                                            minWidth: 52,
                                            cursor: canJump
                                                ? "pointer"
                                                : "default",
                                            "&:hover": canJump
                                                ? { opacity: 0.75 }
                                                : undefined,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 22,
                                                height: 22,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.65rem",
                                                fontWeight: 700,
                                                bgcolor: isActive
                                                    ? "primary.main"
                                                    : isDone
                                                      ? "success.main"
                                                      : "action.disabledBackground",
                                                color:
                                                    isActive || isDone
                                                        ? "#fff"
                                                        : "text.disabled",
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            {isDone ? "✓" : idx + 1}
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: isActive
                                                    ? 700
                                                    : 400,
                                                color: isActive
                                                    ? "primary.main"
                                                    : isDone
                                                      ? "text.secondary"
                                                      : "text.disabled",
                                                whiteSpace: "nowrap",
                                                fontSize: "0.62rem",
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                    {idx < STEP_LABELS.length - 1 && (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                height: 2,
                                                bgcolor:
                                                    idx < activeStep
                                                        ? "success.main"
                                                        : "action.disabledBackground",
                                                mx: 0.5,
                                                mb: 2,
                                                transition: "all 0.2s",
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {activeStep === TOTAL_STEPS ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <Typography variant="h6">
                            RF Test Report{" "}
                            {loadedReportId ? "updated" : "saved"} successfully!
                        </Typography>
                    </Box>
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    gap: 0.5,
                    px: fullScreen ? 1.5 : 3,
                    pb: fullScreen
                        ? "max(8px, env(safe-area-inset-bottom))"
                        : 1.5,
                }}
            >
                {activeStep === TOTAL_STEPS ? (
                    // Completion screen — just close
                    <Button onClick={onClose} variant="contained">
                        Close
                    </Button>
                ) : (
                    // Wizard steps 0–6
                    <>
                        <Button
                            onClick={handleBack}
                            disabled={loading || activeStep === 0}
                            size={fullScreen ? "small" : "medium"}
                        >
                            Back
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        {isEditing &&
                            (fullScreen ? (
                                <Tooltip title="Save Changes">
                                    <span>
                                        <IconButton
                                            onClick={handleQuickSave}
                                            disabled={loading || !isDirty}
                                            color="success"
                                            sx={{
                                                border: 1,
                                                borderColor: isDirty
                                                    ? "success.main"
                                                    : "divider",
                                            }}
                                        >
                                            {loading ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <SaveIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button
                                    onClick={handleQuickSave}
                                    disabled={loading || !isDirty}
                                    color="success"
                                    variant="outlined"
                                    startIcon={
                                        loading ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <SaveIcon fontSize="small" />
                                        )
                                    }
                                >
                                    Save Changes
                                </Button>
                            ))}
                        <Button
                            onClick={requestClose}
                            disabled={loading}
                            size={fullScreen ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color={activeStep === 6 ? "success" : "primary"}
                            onClick={() =>
                                activeStep === 6 ? handleSave() : handleNext()
                            }
                            disabled={loading || isNextDisabled()}
                            size={fullScreen ? "small" : "medium"}
                            startIcon={
                                loading ? <CircularProgress size={16} /> : null
                            }
                        >
                            {activeStep === 6
                                ? loadedReportId
                                    ? "Update"
                                    : "Save"
                                : "Next"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>

        <Dialog
            open={unsavedDialogOpen}
            onClose={() => setUnsavedDialogOpen(false)}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You have unsaved changes to this report. Would you like to
                    save them before closing?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => setUnsavedDialogOpen(false)}
                    disabled={loading}
                >
                    Keep Editing
                </Button>
                <Button
                    color="error"
                    onClick={() => {
                        setUnsavedDialogOpen(false);
                        onClose();
                    }}
                    disabled={loading}
                >
                    Discard
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        setUnsavedDialogOpen(false);
                        handleQuickSave();
                    }}
                    disabled={loading}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={Boolean(pendingCopyReport)}
            onClose={() => setPendingCopyReport(null)}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Replace Floor Plan?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This will replace the current floor plan with the one from{" "}
                    {pendingCopyReport?.report_number ||
                        `Report #${pendingCopyReport?.id}`}
                    . This cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPendingCopyReport(null)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={confirmCopyFloorPlan}
                    autoFocus
                >
                    Replace
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
